import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import { apiDate, localDate } from '@/platform/lib/utils';
import {
    Button,
    Form,
    Input,
    Segment,
    SegmentContent,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import {
    PencilIcon,
    PlusIcon,
    TrashIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import { useForm, usePage } from '@inertiajs/react';
import { useState } from 'react';
import EmployeeShowLayout from './layouts/EmployeeShowLayout';

const ShowView = () => {
    const [isEditing, setIsEditing] = useState(false);

    const { employee } = usePage().props;
    const { data, setData, patch, errors, processing, resetAndClearErrors } =
        useForm(employee);

    const toggleEditing = () => {
        if (!isEditing) {
            setIsEditing(true);

            return;
        }

        resetAndClearErrors();
        setIsEditing(false);
    };

    const addEmployment = () =>
        setData('employments', [
            ...(data?.employments || []),
            { staff_number: '', starts_at: '', ends_at: '' },
        ]);

    const updateEmployment = (index, key, value) =>
        setData(
            'employments',
            data.employments.map((employment, i) =>
                i === index ? { ...employment, [key]: value } : employment
            )
        );

    const removeEmployment = index =>
        setData(
            'employments',
            data.employments.filter((_, i) => i !== index)
        );

    const handleSubmit = () => {
        patch(`/customers/${employee.customer_id}/employees/${employee.id}`, {
            reset: ['employee', 'base_employee', 'navigation'],
            onSuccess: () => setIsEditing(false),
        });
    };

    return (
        <>
            <Form onSubmit={handleSubmit}>
                <Segment>
                    <SegmentHeader header='Werknemer'>
                        <Button type='button' onClick={toggleEditing}>
                            {isEditing ? (
                                <XMarkIcon className='size-4' />
                            ) : (
                                <PencilIcon className='size-4' />
                            )}
                        </Button>
                    </SegmentHeader>
                    <SegmentContent>
                        <Table striped>
                            <TableBody>
                                <TableRow>
                                    <TableCell className='font-bold'>
                                        BSN
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {employee.bsn}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className='font-bold'>
                                        Initialen
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {isEditing ? (
                                            <Input
                                                value={data?.initials || ''}
                                                onChange={event =>
                                                    setData(
                                                        'initials',
                                                        event.target.value
                                                    )
                                                }
                                                error={errors?.initials}
                                            />
                                        ) : (
                                            employee?.initials
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className='font-bold'>
                                        Voornaam
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {isEditing ? (
                                            <Input
                                                value={data?.first_name || ''}
                                                onChange={event =>
                                                    setData(
                                                        'first_name',
                                                        event.target.value
                                                    )
                                                }
                                                error={errors?.first_name}
                                            />
                                        ) : (
                                            employee?.first_name
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className='font-bold'>
                                        Achternaam
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {isEditing ? (
                                            <Input
                                                value={data?.last_name || ''}
                                                onChange={event =>
                                                    setData(
                                                        'last_name',
                                                        event.target.value
                                                    )
                                                }
                                                error={errors?.last_name}
                                            />
                                        ) : (
                                            employee?.last_name
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className='font-bold'>
                                        Tussenvoegsel
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {isEditing ? (
                                            <Input
                                                value={data?.prefix || ''}
                                                onChange={event =>
                                                    setData(
                                                        'prefix',
                                                        event.target.value
                                                    )
                                                }
                                                error={errors?.prefix}
                                            />
                                        ) : (
                                            employee?.prefix
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className='font-bold'>
                                        Geboortedatum
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {isEditing ? (
                                            <Input
                                                type='date'
                                                value={
                                                    apiDate(data?.birth_date) ||
                                                    ''
                                                }
                                                onChange={event =>
                                                    setData(
                                                        'birth_date',
                                                        event.target.value
                                                    )
                                                }
                                                error={errors?.birth_date}
                                            />
                                        ) : (
                                            localDate(employee?.birth_date)
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </SegmentContent>
                </Segment>

                <Segment>
                    <SegmentHeader header='Dienstverbanden' />
                    <SegmentContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>Pers.nr.</TableHeaderCell>
                                    <TableHeaderCell>In dienst</TableHeaderCell>
                                    <TableHeaderCell textAlign='right'>
                                        Uit dienst
                                    </TableHeaderCell>
                                    {isEditing && <TableHeaderCell />}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data?.employments?.length === 0 && (
                                    <NoDataRow
                                        numberOfColumns={isEditing ? 4 : 3}
                                    />
                                )}

                                {data?.employments?.map((employment, index) => (
                                    <TableRow
                                        key={employment.id ?? `new-${index}`}
                                    >
                                        <TableCell>
                                            {isEditing ? (
                                                <Input
                                                    value={
                                                        employment?.staff_number ||
                                                        ''
                                                    }
                                                    onChange={event =>
                                                        updateEmployment(
                                                            index,
                                                            'staff_number',
                                                            event.target.value
                                                        )
                                                    }
                                                    error={
                                                        errors?.[
                                                            `employments.${index}.staff_number`
                                                        ]
                                                    }
                                                />
                                            ) : (
                                                employment?.staff_number
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {isEditing ? (
                                                <Input
                                                    type='date'
                                                    disabled={Boolean(
                                                        employment?.id
                                                    )}
                                                    value={
                                                        apiDate(
                                                            employment?.starts_at
                                                        ) || ''
                                                    }
                                                    onChange={event =>
                                                        updateEmployment(
                                                            index,
                                                            'starts_at',
                                                            event.target.value
                                                        )
                                                    }
                                                    error={
                                                        errors?.[
                                                            `employments.${index}.starts_at`
                                                        ]
                                                    }
                                                />
                                            ) : (
                                                localDate(employment?.starts_at)
                                            )}
                                        </TableCell>
                                        <TableCell textAlign='right'>
                                            {isEditing ? (
                                                <Input
                                                    type='date'
                                                    value={
                                                        apiDate(
                                                            employment?.ends_at
                                                        ) || ''
                                                    }
                                                    onChange={event =>
                                                        updateEmployment(
                                                            index,
                                                            'ends_at',
                                                            event.target.value
                                                        )
                                                    }
                                                    error={
                                                        errors?.[
                                                            `employments.${index}.ends_at`
                                                        ]
                                                    }
                                                />
                                            ) : (
                                                localDate(
                                                    employment?.ends_at
                                                ) || '-'
                                            )}
                                        </TableCell>
                                        {isEditing && (
                                            <TableCell textAlign='right'>
                                                <Button
                                                    type='button'
                                                    variant='danger'
                                                    onClick={() =>
                                                        removeEmployment(index)
                                                    }
                                                    disabled={
                                                        data?.employments
                                                            ?.length === 1
                                                    }
                                                >
                                                    <TrashIcon className='size-4' />
                                                </Button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                                {isEditing && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            textAlign='right'
                                        >
                                            <Button
                                                type='button'
                                                onClick={addEmployment}
                                            >
                                                <PlusIcon className='size-4' />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </SegmentContent>
                </Segment>

                {isEditing && (
                    <Segment floating>
                        <SegmentContent right>
                            <Button
                                type='submit'
                                variant='primary'
                                loading={processing}
                            >
                                Opslaan
                            </Button>
                        </SegmentContent>
                    </Segment>
                )}
            </Form>
        </>
    );
};

ShowView.layout = page => <EmployeeShowLayout>{page}</EmployeeShowLayout>;

export default ShowView;
